import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  CircularProgress,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Layout from '../components/Layout';
import { getCurrentEnvironment } from '../config/environment';
import api from '../services/api';

// Styled components - matching AdminUserManagement styling
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#F7FAFC',
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: 280, // Width of main sidebar (80px) + people submenu (200px)
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(2),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '8px 16px',
  marginRight: theme.spacing(1),
}));

const SubscriptionChip = styled(Chip)<{ tier: string }>(({ theme, tier }) => ({
  borderRadius: 12,
  fontSize: '0.7rem',
  fontWeight: 600,
  backgroundColor: tier === 'Enterprise' ? '#E6FFFA' : 
                   tier === 'Pro' ? '#FEF5E7' : 
                   tier === 'Essential' ? '#F7FAFC' : '#E2E8F0',
  color: tier === 'Enterprise' ? '#234E52' : 
         tier === 'Pro' ? '#744210' : 
         tier === 'Essential' ? '#4A5568' : '#4A5568',
}));

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => ({
  borderRadius: 16,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: status === 'active' ? '#C6F6D5' : 
                   status === 'inactive' ? '#FED7D7' : 
                   status === 'suspended' ? '#FEEBC8' : '#E2E8F0',
  color: status === 'active' ? '#22543D' : 
         status === 'inactive' ? '#742A2A' : 
         status === 'suspended' ? '#744210' : '#4A5568',
}));

const BillingChip = styled(Chip)<{ status: string }>(({ theme, status }) => ({
  borderRadius: 12,
  fontSize: '0.7rem',
  fontWeight: 600,
  backgroundColor: status === 'monthly' ? '#BEE3F8' : 
                   status === 'yearly' ? '#E9D8FD' : '#E2E8F0',
  color: status === 'monthly' ? '#2A4365' : 
         status === 'yearly' ? '#553C9A' : '#4A5568',
}));

// Types - updated to match exact company schema
interface Company {
  id: string;
  user_id?: string;
  admin_id?: string;
  company_name: string;
  subscription_plan: 'Essential' | 'Pro' | 'Enterprise';
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: number;
  country?: string;
  timezone?: string;
  ein?: number;
  entity_type?: string;
  stripe_billing_id?: string;
  billing_cycle: 'monthly' | 'yearly';
  billing_status: 'active' | 'inactive';
  is_suspended: boolean;
  is_deleted: boolean;
  created_at?: string;
  updated_at?: string;
  stripe_subscription?: {
    id: string | null;
    status: string;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    plan: string | null;
  } | null;
}

interface CompanyFormData {
  company_name: string;
  subscription_plan: 'Essential' | 'Pro' | 'Enterprise';
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  timezone: string;
  ein: string;
  entity_type: string;
  billing_cycle: 'monthly' | 'yearly';
  billing_status: 'active' | 'inactive';
  is_suspended: boolean;
}

interface EnvironmentInfo {
  environment: string;
  dev_mode: boolean;
  proptrak_api_base: string;
  aletheia_database_configured: boolean;
  proptrak_database_configured: boolean;
}

// Options for form fields
const subscriptionOptions = [
  { value: 'Essential', label: 'Essential' },
  { value: 'Pro', label: 'Pro' },
  { value: 'Enterprise', label: 'Enterprise' }
];

const stateOptions = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

const countryOptions = [
  { value: 'United States', label: 'United States' },
  { value: 'Canada', label: 'Canada' }
];

const timezoneOptions = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' }
];

const entityTypeOptions = [
  { value: 'LLC', label: 'LLC' },
  { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'Corporation', label: 'Corporation' },
  { value: 'Non-Profit', label: 'Non-Profit' },
  { value: 'Other', label: 'Other' }
];

const billingCycleOptions = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' }
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const CompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  // State
  const [companies, setCompanies] = useState<Company[]>([]);
  const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfo | null>(null);
  const [environment, setEnvironment] = useState<'staging' | 'production'>('staging');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [operationLoading, setOperationLoading] = useState<{ [key: string]: boolean }>({});
  const [lastDataRefresh, setLastDataRefresh] = useState<Date | null>(null);
  
  // Dialog states
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [environmentDialogOpen, setEnvironmentDialogOpen] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<{ id: string; name: string } | null>(null);
  
  // Form states
  const [companyForm, setCompanyForm] = useState<CompanyFormData>({
    company_name: '',
    subscription_plan: 'Essential',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    timezone: '',
    ein: '',
    entity_type: '',
    billing_cycle: 'monthly',
    billing_status: 'active',
    is_suspended: false,
  });

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // API base URL - use environment configuration
  const API_BASE = getCurrentEnvironment().aletheiaAdminApi;

  // Load data
  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading data from API_BASE:', API_BASE);
      
      const [companiesData, envData] = await Promise.all([
        api.get<Company[]>('/api/proptrak/companies'),
        api.get<EnvironmentInfo>('/api/admin/environment/')
      ]);

      console.log('API responses: Success');

      setCompanies(companiesData || []);
      setEnvironmentInfo(envData);
      
      // Update environment state based on backend response
      if (envData.environment && (envData.environment === 'staging' || envData.environment === 'production')) {
        setEnvironment(envData.environment);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(`Network error: ${errorMessage}`);
      console.error('Error loading data:', err);
      // No fallback data - API must be working
    } finally {
      setLoading(false);
      setLastDataRefresh(new Date());
    }
  };

  const handleEnvironmentChange = async (newEnvironment: 'staging' | 'production') => {
    try {
      await api.post<{ message: string }>('/api/admin/switch-environment', { environment: newEnvironment });
      
      setEnvironment(newEnvironment);
      // Reload data after environment switch
      await loadData();
      setSuccess(`Switched to ${newEnvironment} environment`);
    } catch (error) {
      console.error('Error switching environment:', error);
      setError(error instanceof Error ? error.message : 'Failed to switch environment');
    }
  };

  // Company operations
  const handleCreateCompany = async () => {
    try {
      const responseData = await api.post<{ company: Company; message: string }>('/api/proptrak/companies', companyForm);
        
        // Add the new company to local state immediately for better UX
        if (responseData.company) {
          const newCompany: Company = {
            id: responseData.company.id,
            company_name: companyForm.company_name,
            subscription_plan: companyForm.subscription_plan,
            address_line_1: companyForm.address_line_1,
            address_line_2: companyForm.address_line_2,
            city: companyForm.city,
            state: companyForm.state,
            zip_code: parseInt(companyForm.zip_code) || undefined,
            country: companyForm.country,
            timezone: companyForm.timezone,
            ein: parseInt(companyForm.ein) || undefined,
            entity_type: companyForm.entity_type,
            billing_cycle: companyForm.billing_cycle,
            billing_status: companyForm.billing_status,
            is_suspended: companyForm.is_suspended,
            is_deleted: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setCompanies(prevCompanies => [newCompany, ...prevCompanies]);
        }

        setSuccess('Company created successfully');
        setShowCompanyDialog(false);
        resetCompanyForm();
        
        // Also refresh from server to ensure consistency
        setTimeout(() => {
          loadData();
        }, 500);
    } catch (error) {
      console.error('Error creating company:', error);
      setError(error instanceof Error ? error.message : 'Failed to create company');
    }
  };

  const handleUpdateCompany = async () => {
    if (!editingCompany) return;

    const operationKey = `update_${editingCompany.id}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      await api.put<{ message: string }>(`/api/proptrak/companies/${editingCompany.id}`, companyForm);
        // Update the local state immediately for better UX
        const updatedCompanies = companies.map(company => {
          if (company.id === editingCompany.id) {
            return {
              ...company,
              company_name: companyForm.company_name,
              subscription_plan: companyForm.subscription_plan,
              address_line_1: companyForm.address_line_1,
              address_line_2: companyForm.address_line_2,
              city: companyForm.city,
              state: companyForm.state,
              zip_code: parseInt(companyForm.zip_code) || undefined,
              country: companyForm.country,
              timezone: companyForm.timezone,
              ein: parseInt(companyForm.ein) || undefined,
              entity_type: companyForm.entity_type,
              billing_cycle: companyForm.billing_cycle,
              billing_status: companyForm.billing_status,
              is_suspended: companyForm.is_suspended,
              updated_at: new Date().toISOString()
            };
          }
          return company;
        });
        setCompanies(updatedCompanies);

        setSuccess('Company updated successfully');
        setShowCompanyDialog(false);
        setEditingCompany(null);
        resetCompanyForm();
        
        // Also refresh from server to ensure consistency
        setTimeout(() => {
          loadData();
        }, 500);
    } catch (error) {
      console.error('Error updating company:', error);
      setError(error instanceof Error ? error.message : 'Failed to update company');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  const handleDeleteCompany = async (companyId: string) => {
    const operationKey = `delete_${companyId}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      await api.delete<{ message: string }>(`/api/proptrak/companies/${companyId}`);
      
      // Remove company from local state immediately
      setCompanies(prevCompanies => prevCompanies.filter(company => company.id !== companyId));
      setSuccess('Company deleted successfully');
      setShowDeleteConfirmDialog(false);
      
      // Also refresh from server to ensure consistency
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Error deleting company:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete company');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  const handleSuspendCompany = async (companyId: string) => {
    const operationKey = `suspend_${companyId}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      await api.post<{ message: string }>(`/api/proptrak/companies/${companyId}/suspend`);
      
      // Update company status in local state immediately
      setCompanies(prevCompanies => prevCompanies.map(company => 
        company.id === companyId ? { ...company, is_suspended: true } : company
      ));
      setSuccess('Company suspended successfully');
      
      // Also refresh from server to ensure consistency
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Error suspending company:', error);
      setError(error instanceof Error ? error.message : 'Failed to suspend company');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  const handleActivateCompany = async (companyId: string) => {
    const operationKey = `activate_${companyId}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));

    try {
      await api.post<{ message: string }>(`/api/proptrak/companies/${companyId}/activate`);
      
      // Update company status in local state immediately
      setCompanies(prevCompanies => prevCompanies.map(company => 
        company.id === companyId ? { ...company, is_suspended: false } : company
      ));
      setSuccess('Company activated successfully');
      
      // Also refresh from server to ensure consistency
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Error activating company:', error);
      setError(error instanceof Error ? error.message : 'Failed to activate company');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  // Form handlers
  const resetCompanyForm = () => {
    setCompanyForm({
      company_name: '',
      subscription_plan: 'Essential',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      timezone: '',
      ein: '',
      entity_type: '',
      billing_cycle: 'monthly',
      billing_status: 'active',
      is_suspended: false,
    });
  };

  const openCompanyDialog = (company?: Company) => {
    if (company) {
      setEditingCompany(company);
      setCompanyForm({
        company_name: company.company_name,
        subscription_plan: company.subscription_plan,
        address_line_1: company.address_line_1 || '',
        address_line_2: company.address_line_2 || '',
        city: company.city || '',
        state: company.state || '',
        zip_code: company.zip_code?.toString() || '',
        country: company.country || '',
        timezone: company.timezone || '',
        ein: company.ein?.toString() || '',
        entity_type: company.entity_type || '',
        billing_cycle: company.billing_cycle,
        billing_status: company.billing_status,
        is_suspended: company.is_suspended,
      });
    } else {
      setEditingCompany(null);
      resetCompanyForm();
    }
    setShowCompanyDialog(true);
  };

  const openDeleteConfirmDialog = (companyId: string, companyName: string) => {
    setCompanyToDelete({ id: companyId, name: companyName });
    setShowDeleteConfirmDialog(true);
  };

  const closeCompanyDialog = () => {
    setShowCompanyDialog(false);
    setEditingCompany(null);
    resetCompanyForm();
  };

  // Render functions
  const renderCompanyDialog = () => (
    <Dialog 
      open={showCompanyDialog} 
      onClose={closeCompanyDialog} 
      maxWidth="md" 
      fullWidth
      disableEnforceFocus={false}
      disableAutoFocus={false}
    >
      <DialogTitle>
        {editingCompany ? 'Edit Company' : 'Create New Company'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="Company Name"
            value={companyForm.company_name}
            onChange={(e) => setCompanyForm({ ...companyForm, company_name: e.target.value })}
            required
          />
          <FormControl fullWidth>
            <InputLabel>Subscription Plan</InputLabel>
            <Select
              value={companyForm.subscription_plan}
              onChange={(e) => setCompanyForm({ ...companyForm, subscription_plan: e.target.value as any })}
              label="Subscription Plan"
            >
              {subscriptionOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Address Line 1"
            value={companyForm.address_line_1}
            onChange={(e) => setCompanyForm({ ...companyForm, address_line_1: e.target.value })}
          />
          <TextField
            fullWidth
            label="Address Line 2"
            value={companyForm.address_line_2}
            onChange={(e) => setCompanyForm({ ...companyForm, address_line_2: e.target.value })}
          />
          <TextField
            fullWidth
            label="City"
            value={companyForm.city}
            onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
          />
          <FormControl fullWidth>
            <InputLabel>State</InputLabel>
            <Select
              value={companyForm.state}
              onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })}
              label="State"
            >
              {stateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="ZIP Code"
            value={companyForm.zip_code}
            onChange={(e) => setCompanyForm({ ...companyForm, zip_code: e.target.value })}
          />
          <FormControl fullWidth>
            <InputLabel>Country</InputLabel>
            <Select
              value={companyForm.country}
              onChange={(e) => setCompanyForm({ ...companyForm, country: e.target.value })}
              label="Country"
            >
              {countryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Timezone</InputLabel>
            <Select
              value={companyForm.timezone}
              onChange={(e) => setCompanyForm({ ...companyForm, timezone: e.target.value })}
              label="Timezone"
            >
              {timezoneOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="EIN"
            value={companyForm.ein}
            onChange={(e) => setCompanyForm({ ...companyForm, ein: e.target.value })}
            placeholder="12-3456789"
          />
          <FormControl fullWidth>
            <InputLabel>Entity Type</InputLabel>
            <Select
              value={companyForm.entity_type}
              onChange={(e) => setCompanyForm({ ...companyForm, entity_type: e.target.value })}
              label="Entity Type"
            >
              {entityTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Billing Cycle</InputLabel>
            <Select
              value={companyForm.billing_cycle}
              onChange={(e) => setCompanyForm({ ...companyForm, billing_cycle: e.target.value as any })}
              label="Billing Cycle"
            >
              {billingCycleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Billing Status</InputLabel>
            <Select
              value={companyForm.billing_status}
              onChange={(e) => setCompanyForm({ ...companyForm, billing_status: e.target.value as any })}
              label="Billing Status"
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={companyForm.is_suspended}
                  onChange={(e) => setCompanyForm({ ...companyForm, is_suspended: e.target.checked })}
                />
              }
              label="Company Suspended"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeCompanyDialog}>Cancel</Button>
        <Button
          onClick={editingCompany ? handleUpdateCompany : handleCreateCompany}
          variant="contained"
          color="primary"
          disabled={editingCompany ? operationLoading[`update_${editingCompany.id}`] : false}
          startIcon={editingCompany && operationLoading[`update_${editingCompany.id}`] ? (
            <CircularProgress size={16} />
          ) : undefined}
        >
          {editingCompany ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEnvironmentDialog = () => (
    <Dialog 
      open={environmentDialogOpen} 
      onClose={() => setEnvironmentDialogOpen(false)} 
      maxWidth="md" 
      fullWidth
      disableEnforceFocus={false}
      disableAutoFocus={false}
    >
      <DialogTitle>Environment Configuration</DialogTitle>
      <DialogContent>
        {environmentInfo && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Current Environment</Typography>
                <Typography variant="body2" color="text.secondary">
                  Environment: {environmentInfo.environment}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Development Mode: {environmentInfo.dev_mode ? 'Enabled' : 'Disabled'}
                </Typography>
                                 <Typography variant="body2" color="text.secondary">
                   PropTrak API: {environmentInfo.proptrak_api_base}
                 </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Database Status</Typography>
                <Typography variant="body2" color="text.secondary">
                  Aletheia Database: {environmentInfo.aletheia_database_configured ? '✅ Connected' : '❌ Not Connected'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PropTrak Database: {environmentInfo.proptrak_database_configured ? '✅ Connected' : '❌ Not Connected'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEnvironmentDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <PageContainer>
        <Layout>
          <MainContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
              <CircularProgress />
            </Box>
          </MainContent>
        </Layout>
      </PageContainer>
    );
  }

  const filteredCompanies = companies.filter(company =>
    company.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.state?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <PageContainer>
      <Layout>
        <MainContent>
          {/* Header */}
          <Header>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A202C' }}>
              Company Management
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Environment Toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Environment:
                </Typography>
                <ToggleButtonGroup
                  value={environment}
                  exclusive
                  onChange={(_, newEnvironment) => {
                    if (newEnvironment) {
                      handleEnvironmentChange(newEnvironment);
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="staging">Staging</ToggleButton>
                  <ToggleButton value="production">Production</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              
              <Tooltip title="Environment Settings">
                <IconButton onClick={() => setEnvironmentDialogOpen(true)}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <ActionButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openCompanyDialog()}
                sx={{ ml: 1 }}
              >
                Add Company
              </ActionButton>
            </Box>
          </Header>

          {/* Environment Info */}
          {environmentInfo && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Current Environment:</strong> {environmentInfo.environment} | 
                <strong>PropTrak API:</strong> {environmentInfo.proptrak_api_base} | 
                <strong>Dev Mode:</strong> {environmentInfo.dev_mode ? 'Yes' : 'No'}
              </Typography>
            </Alert>
          )}

          {/* Stats Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {companies.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Companies
                </Typography>
              </CardContent>
            </StyledCard>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {companies.filter(c => c.subscription_plan === 'Enterprise').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enterprise
                </Typography>
              </CardContent>
            </StyledCard>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {companies.filter(c => c.subscription_plan === 'Pro').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pro
                </Typography>
              </CardContent>
            </StyledCard>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  {companies.filter(c => c.subscription_plan === 'Essential').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Essential
                </Typography>
              </CardContent>
            </StyledCard>
          </Box>

          {/* Search and Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <TextField
              placeholder="Search companies..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#A0AEC0' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            <Button
              size="small"
              onClick={loadData}
              disabled={loading}
              startIcon={loading ? <RefreshIcon /> : <RefreshIcon />}
              sx={{ 
                animation: loading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>

          {/* Companies Table */}
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6">
                    Companies
                  </Typography>
                  {lastDataRefresh && (
                    <Typography variant="caption" color="text.secondary">
                      Last refreshed: {lastDataRefresh.toLocaleTimeString()}
                    </Typography>
                  )}
                </Box>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Company Name</TableCell>
                      <TableCell>Subscription</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Billing</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCompanies.map((company) => (
                      <TableRow key={company.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} color="text.secondary">
                            #{company.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box 
                            onClick={() => navigate(`/companies/${company.id}`)}
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: 'primary.main' } }}
                          >
                            <Typography variant="body2" fontWeight={500}>
                              {company.company_name}
                            </Typography>
                            {company.entity_type && (
                              <Typography variant="caption" color="text.secondary" sx={{ 
                                display: 'inline-block', 
                                backgroundColor: 'grey.100', 
                                px: 1, 
                                py: 0.25, 
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                fontWeight: 500
                              }}>
                                {company.entity_type}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Updated: {company.updated_at ? new Date(company.updated_at).toLocaleDateString() : 'N/A'} {company.updated_at ? new Date(company.updated_at).toLocaleTimeString() : ''}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <SubscriptionChip 
                            label={company.subscription_plan} 
                            tier={company.subscription_plan}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            {company.city && company.state && (
                              <Typography variant="body2">
                                {company.city}, {company.state}
                              </Typography>
                            )}
                            {company.country && (
                              <Typography variant="caption" color="text.secondary">
                                {company.country}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <BillingChip 
                              label={company.billing_cycle} 
                              status={company.billing_cycle}
                              size="small"
                            />
                            {(() => {
                              const stripeStatus = company.stripe_subscription?.status;
                              const isActive = stripeStatus === 'active' || stripeStatus === 'trialing';
                              const label = isActive ? 'Active' : (stripeStatus === 'past_due' ? 'Past Due' : 'Inactive');
                              const status = isActive ? 'active' : (stripeStatus === 'past_due' ? 'suspended' : 'inactive'); // using 'suspended' color for past due
                              
                              return (
                                <StatusChip 
                                  label={label} 
                                  status={status}
                                  size="small"
                                />
                              );
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <StatusChip 
                            label={company.is_suspended ? 'Suspended' : 'Active'} 
                            status={company.is_suspended ? 'suspended' : 'active'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit Company">
                              <IconButton 
                                size="small" 
                                onClick={() => openCompanyDialog(company)}
                                disabled={operationLoading[`update_${company.id}`]}
                              >
                                {operationLoading[`update_${company.id}`] ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <EditIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                            {company.is_suspended ? (
                              <Tooltip title="Activate Company">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleActivateCompany(company.id)}
                                  disabled={operationLoading[`activate_${company.id}`]}
                                >
                                  {operationLoading[`activate_${company.id}`] ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <CheckCircleIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Suspend Company">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleSuspendCompany(company.id)}
                                  disabled={operationLoading[`suspend_${company.id}`]}
                                >
                                  {operationLoading[`suspend_${company.id}`] ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <BlockIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete Company">
                              <IconButton 
                                size="small" 
                                onClick={() => openDeleteConfirmDialog(company.id, company.company_name)}
                                disabled={operationLoading[`delete_${company.id}`]}
                              >
                                {operationLoading[`delete_${company.id}`] ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>

          {/* Dialogs */}
          {renderCompanyDialog()}
          {renderEnvironmentDialog()}

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={showDeleteConfirmDialog}
            onClose={() => setShowDeleteConfirmDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete the company <strong>{companyToDelete?.name}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This action cannot be undone. The company will be permanently removed from the system.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setShowDeleteConfirmDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  if (companyToDelete) {
                    handleDeleteCompany(companyToDelete.id);
                    setShowDeleteConfirmDialog(false);
                  }
                }}
                color="error"
                variant="contained"
              >
                Delete Company
              </Button>
            </DialogActions>
          </Dialog>

          {/* Notifications */}
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
          >
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Snackbar>

          <Snackbar
            open={!!success}
            autoHideDuration={6000}
            onClose={() => setSuccess(null)}
          >
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          </Snackbar>
        </MainContent>
      </Layout>
    </PageContainer>
  );
};

export default CompaniesPage; 